import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ArrowRight, Check, X, AlertCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { DecisionFramework } from '../components/DecisionFramework';
import { useLanguage } from '../i18n/LanguageContext';
import { getOffer, pick, withSubId, type Lang } from '../offers/config';
import { videoFor } from '../offers/videos';
import { highlightsFor } from '../offers/highlights';
import { goPath, withQuery } from '../offers/links';

// true  -> el botón va a /go/:slug (ruta interna, dispara AffiliateClick)
// false -> el botón va directo al link de impact (una petición menos,
//          resiste si el JS de la SPA falla)
const USE_GO_ROUTE = true;
const UI = {
  es: {
    notFound: 'Esta página no existe',
    backHome: 'Volver al inicio',
    updated: 'Actualizado',
    readTime: (n: number) => `Lectura de ${n} min`,
    confidence: 'Nivel de Confianza',
    disclosureLabel: 'Divulgación:',
    disclosure: 'Blinkblis recibe una comisión si abres una cuenta a través de los enlaces de esta página. No te cuesta nada adicional y no cambia lo que recomendamos.',
    disclosureLink: 'Cómo trabajamos',
    problemTitle: 'El problema real',
    problem: ['Si llegaste a Estados Unidos hace poco, o simplemente nunca pediste crédito, estás en una situación que el sistema no resuelve bien: para que te den crédito necesitas historial, y para tener historial necesitas crédito.', 'No es un problema de ingresos. Puedes ganar bien, pagar todo a tiempo y en efectivo, y aun así aparecer como invisible ante los burós.', 'En Estados Unidos pagar bien no construye historial. Solo construye historial aquello que se reporta.', 'Esa es la única puerta: necesitas una cuenta que reporte.'],
    whatLabel: 'Qué hace realmente un credit builder',
    what: 'Un producto de credit builder existe para una sola cosa: crear actividad que se reporte a los burós de crédito, sin exigirte historial previo para abrirla. Abres una cuenta pequeña, haces pagos mensuales, y el proveedor los reporta. Lo que se construye ahí es historial de pagos, el factor de mayor peso en la mayoría de los modelos de puntaje.',
    bestForTitle: 'Para quién puede ser esto',
    bestForIntro: 'Esto puede encajar contigo si:',
    glanceTitle: 'Kovo en resumen',
    glanceBestFor: 'Recomendado para',
    glanceNotFor: 'No recomendado para',
    howTitle: 'Cómo funciona, en 3 pasos',
    notDoTitle: 'Lo que NO hace',
    notDo: ['No borra ni corrige información negativa existente', 'No garantiza aprobación en ningún producto futuro', 'No produce resultados inmediatos', 'No sustituye resolver colecciones ni pagos atrasados'],
    notDoNote: 'Cualquiera que te prometa un número de puntos en un plazo determinado te está ofreciendo algo que no puede entregar. Los resultados dependen de tu situación particular y nadie puede garantizarlos.',
    videoTitle: 'Explicado en video',
    videoNote: 'Video producido por Blinkblis.',
    prosConsTitle: 'A favor y en contra',
    pros: 'A favor',
    cons: 'En contra',
    affiliateNote: (network: string) => `Enlace de afiliado vía ${network}. Blinkblis recibe una comisión si abres una cuenta. Tu costo es el mismo.`,
    notForTitle: 'Para quién NO es esto',
    notForIntro: 'Te lo decimos claro porque preferimos que no abras una cuenta que no necesitas. Esto no es para ti si:',
    faqTitle: 'Preguntas frecuentes',
    criteriaTitle: 'Cómo lo evaluamos',
    criteria: [{
      t: 'Costo real mensual',
      d: 'Cuánto pagas y qué recibes exactamente. Un producto barato que no reporta no sirve de nada.'
    }, {
      t: 'Requisitos de entrada',
      d: '¿Pide historial previo? ¿Revisión dura? ¿Depósito grande? Menos barreras, más útil para quien empieza.'
    }, {
      t: 'A cuántos burós reporta',
      d: 'Reportar a un solo buró construye una fracción del historial que construye reportar a todos. Es el criterio que más separa a un producto de otro.'
    }, {
      t: 'Transparencia y salida',
      d: '¿Puedes cancelar sin penalización? ¿Los términos están claros antes de dar tus datos?'
    }],
    finalTitle: '¿Listo para empezar?',
    finalText: 'Toma unos minutos desde el teléfono. Si después de leer esto crees que no es para ti, no lo abras: también es una decisión válida.',
    footerNote: 'Este contenido es informativo y no constituye asesoría financiera. Los términos de los productos cambian; verifica siempre directamente con el proveedor. Ver',
    methodology: 'Metodología',
    disclosurePage: 'Divulgación de Afiliados',
    and: 'y'
  },
  en: {
    notFound: 'This page does not exist',
    backHome: 'Back to home',
    updated: 'Updated',
    readTime: (n: number) => `${n} min read`,
    confidence: 'Confidence Level',
    disclosureLabel: 'Disclosure:',
    disclosure: 'Blinkblis earns a commission if you open an account through the links on this page. It costs you nothing extra and it does not change what we recommend.',
    disclosureLink: 'How we work',
    problemTitle: 'The real problem',
    problem: ['If you recently arrived in the United States, or you simply never borrowed, you are in a situation the system handles badly: to get credit you need history, and to have history you need credit.', 'It is not an income problem. You can earn well, pay everything on time and in cash, and still be invisible to the bureaus.', 'In the United States, paying well does not build history. Only what gets reported builds history.', 'That is the only door: you need an account that reports.'],
    whatLabel: 'What a credit builder actually does',
    what: 'A credit builder product exists for one thing: to create activity that gets reported to the credit bureaus, without requiring prior history to open it. You open a small account, you make monthly payments, and the provider reports them. What gets built there is payment history, the heaviest factor in most scoring models.',
    bestForTitle: 'Who this may be for',
    bestForIntro: 'This may be a fit if:',
    glanceTitle: 'Kovo at a glance',
    glanceBestFor: 'Best for',
    glanceNotFor: 'Not for',
    howTitle: 'How it works, in 3 steps',
    notDoTitle: 'What it does NOT do',
    notDo: ['It does not erase or correct existing negative information', 'It does not guarantee approval for any future product', 'It does not produce immediate results', 'It does not replace resolving collections or late payments'],
    notDoNote: 'Anyone promising you a specific number of points in a specific timeframe is offering something they cannot deliver. Results depend on your particular situation and nobody can guarantee them.',
    videoTitle: 'Explained on video',
    videoNote: 'Video produced by Blinkblis.',
    prosConsTitle: 'Pros and cons',
    pros: 'Pros',
    cons: 'Cons',
    affiliateNote: (network: string) => `Affiliate link via ${network}. Blinkblis earns a commission if you open an account. Your cost is the same.`,
    notForTitle: 'Who this is NOT for',
    notForIntro: 'We say it plainly because we would rather you not open an account you do not need. This is not for you if:',
    faqTitle: 'Frequently asked questions',
    criteriaTitle: 'How we evaluate it',
    criteria: [{
      t: 'Real monthly cost',
      d: 'What you pay and what exactly you get. A cheap product that does not report is worth nothing.'
    }, {
      t: 'Entry requirements',
      d: 'Does it require prior history? A hard pull? A large deposit? Fewer barriers, more useful for someone starting out.'
    }, {
      t: 'How many bureaus it reports to',
      d: 'Reporting to a single bureau builds a fraction of the history that reporting to all of them does. This is the criterion that separates one product from another the most.'
    }, {
      t: 'Transparency and exit',
      d: 'Can you cancel without penalty? Are the terms clear before you hand over your data?'
    }],
    finalTitle: 'Ready to start?',
    finalText: 'It takes a few minutes from your phone. If after reading this you think it is not for you, do not open it: that is a valid decision too.',
    footerNote: 'This content is informational and does not constitute financial advice. Product terms change; always verify directly with the provider. See',
    methodology: 'Methodology',
    disclosurePage: 'Affiliate Disclosure',
    and: 'and'
  }
};
export function Offer() {
  const {
    slug
  } = useParams();
  const {
    search
  } = useLocation();
  const {
    language
  } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  const t = UI[lang];
  const offer = getOffer(slug);
  if (!offer) {
    return <div className="min-h-screen bg-background text-white flex flex-col" data-id="element-441" data-mp="32j7tyr76rl77">
        <Navbar data-id="element-442" data-mp="37ag3rocfh8kw" />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center" data-id="element-443" data-mp="37azotdxdkd35">
          <h1 className="font-display text-3xl" data-id="element-444" data-mp="2tjl3v1jwq1tg">{t.notFound}</h1>
          <Link to="/" className="text-accent hover:underline" data-id="element-445" data-mp="2tgyoifqryi0o">
            {t.backHome}
          </Link>
        </div>
        <Footer data-id="element-446" data-mp="3la7n84lprh6a" />
      </div>;
  }
  const href = USE_GO_ROUTE ? goPath(offer.slug, search) : withSubId(offer.affiliateUrl, offer.slug);
  const stuck = pick(offer.stuck, lang);
  const facts = pick(offer.facts, lang);
  const bestFor = pick(offer.bestFor, lang);
  const howItWorks = pick(offer.howItWorks, lang);
  const pros = pick(offer.pros, lang);
  const cons = pick(offer.cons, lang);
  const notFor = pick(offer.notFor, lang);
  const faq = pick(offer.faq, lang);
  const video = videoFor(offer.slug);
  const rawHighlights = highlightsFor(offer.slug);
  const highlights = rawHighlights ? pick(rawHighlights, lang) : [];
  const ctaLabel = pick(offer.ctaLabel, lang);
  const ctaMicro = pick(offer.ctaMicro, lang);
  const ctaDisclaimer = pick(offer.ctaDisclaimer, lang);
  const fireDirect = () => {
    if (USE_GO_ROUTE) return;
    const w = window as any;
    if (typeof w.fbq === 'function') {
      w.fbq('trackCustom', 'AffiliateClick', {
        network: offer.network,
        brand: offer.brand,
        offer: offer.slug
      });
    }
  };
  const Cta = ({
    full = false
  }: {
    full?: boolean;
  }) => <a href={href} rel="nofollow sponsored noopener noreferrer" onClick={fireDirect} className={`${full ? 'w-full' : 'w-full sm:w-auto'} inline-flex items-center justify-center gap-2 bg-accent text-background px-7 h-[56px] rounded-full text-[16px] font-semibold hover:brightness-110 active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60`} data-id="element-447" data-mp="1mnmib2zol34l">
      {ctaLabel}
      <ArrowRight className="w-[18px] h-[18px]" data-id="element-448" data-mp="37zxokepm4l5b" />
    </a>;
  const CtaMicro = () => <p className="mt-3 text-[13px] text-white/35 leading-relaxed" data-id="element-449" data-mp="3i5600iwovzze">{ctaMicro}</p>;
  const Section = ({
    title,
    children
  }: {
    title: string;
    children: React.ReactNode;
  }) => <section className="mb-14" data-id="element-450" data-mp="3qxho3y4bzbc7">
      <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-6" data-id="element-451" data-mp="3qyss9z1oul3u">
        {title}
      </h2>
      {children}
    </section>;
  return <div className="min-h-screen bg-background text-white" data-id="element-452" data-mp="4o6lbqpxi91f">
      <Navbar data-id="element-453" data-mp="3vw17prvgcb1e" />

      <article className="max-w-[760px] mx-auto px-5 sm:px-6 pt-32 md:pt-40 pb-24" data-id="element-454" data-mp="3vwkgoj34jp3a">
        {/* ---------- 1. HERO ---------- */}
        <div className="flex items-center gap-2 text-[13px] text-white/40 mb-8" data-id="element-455" data-mp="dpmp3fm8ncwo">
          <Link to={withQuery('/', search)} className="hover:text-white/70 transition-colors" data-id="element-456" data-mp="9pw1fks0dcwd">
            Blinkblis
          </Link>
          <span data-id="element-457" data-mp="2bgfskowlnv0s">/</span>
          <span className="text-accent" data-id="element-458" data-mp="2bgzc2a1wergx">{pick(offer.category, lang)}</span>
        </div>

        <h1 className="font-display text-[34px] sm:text-5xl font-semibold tracking-[-0.03em] leading-[1.08] mb-6" data-id="element-459" data-mp="eg3anv0gbiei">
          {pick(offer.title, lang)}
        </h1>

        {highlights.length > 0 && <div className="flex flex-wrap gap-2 mb-7" data-id="element-460" data-mp="ocg18k0p9g51">
            {highlights.map(h => <span key={h} className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/[0.07] px-3.5 py-1.5 text-[13px] sm:text-[14px] font-medium text-white/90" data-id="element-461" data-mp="tcu8hzuh8m20">
                <Check className="w-[14px] h-[14px] text-accent shrink-0" data-id="element-462" data-mp="12b5xxyjlxdsv" />
                {h}
              </span>)}
          </div>}

        <p className="text-[18px] sm:text-xl text-white/55 font-light leading-relaxed mb-8" data-id="element-463" data-mp="1c3bp5621br70">
          {pick(offer.subtitle, lang)}
        </p>

        <div className="mb-8" data-id="element-464" data-mp="1h0241q23sgqu">
          <Cta data-id="element-465" data-mp="1l3gvdcvhsjjl" />
          <CtaMicro data-id="element-466" data-mp="1l2yshxebr72p" />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-white/40 mb-8 pb-8 border-b border-white/10" data-id="element-467" data-mp="1l40360rfhrjw">
          <span data-id="element-468" data-mp="31k77qvuyjww3">
            {t.updated}: {pick(offer.updated, lang)}
          </span>
          <span className="text-white/20" data-id="element-469" data-mp="31mjzg0qiplmc">·</span>
          <span data-id="element-470" data-mp="2wm5qmgh3e7n9">{t.readTime(offer.readMinutes)}</span>
          <span className="text-white/20" data-id="element-471" data-mp="2wmxknmv1jxbf">·</span>
          <Link to={withQuery('/metodologia', search)} className="text-accent hover:underline" data-id="element-472" data-mp="2rn1epi2s9vt8">
            {t.confidence} {offer.confidence}%
          </Link>
        </div>

        {/* ---------- 2. DIVULGACIÓN ---------- */}
        <div className="mb-14 border-l-2 border-accent bg-white/[0.03] rounded-r-xl px-5 py-4 text-[15px] leading-relaxed text-white/60" data-id="element-473" data-mp="3fjp2druxq4dd">
          <span className="text-white/85 font-medium" data-id="element-474" data-mp="3ak2odg7a8k3i">
            {t.disclosureLabel}
          </span>{' '}
          {t.disclosure}{' '}
          <Link to={withQuery('/divulgacion-de-afiliados', search)} className="text-accent hover:underline whitespace-nowrap" data-id="element-475" data-mp="bwoq7zs5tz7v">
            {t.disclosureLink} →
          </Link>
        </div>

        {/* ---------- 3. EL PROBLEMA REAL ---------- */}
        <Section title={t.problemTitle} data-id="element-476" data-mp="3rug48cuopf5g">
          <div className="space-y-5 text-[17px] leading-[1.75] text-white/65 font-light" data-id="element-477" data-mp="3dtlv1rztba5q">
            <p data-id="element-478" data-mp="3is6u3o3bvnsl">{t.problem[0]}</p>
            <p data-id="element-479" data-mp="3ithv5g5e2hg0">
              {t.problem[1]}{' '}
              <span className="text-white/90" data-id="element-480" data-mp="3iu9rwuaq2l7v">{t.problem[2]}</span>
            </p>
            <p data-id="element-481" data-mp="aeq4ibwqfx0q">{t.problem[3]}</p>
          </div>

          <div className="mt-7 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-482" data-mp="fbzq1gh03oja">
            <h3 className="text-white font-medium text-[15px] mb-3" data-id="element-483" data-mp="nbydla9zyym">
              {t.whatLabel}
            </h3>
            <p className="text-[15px] text-white/55 leading-relaxed font-light" data-id="element-484" data-mp="5l51u6k72ev7">
              {t.what}
            </p>
          </div>
        </Section>

        {/* ---------- 4. LLEVAS AÑOS Y SIGUES IGUAL ---------- */}
        <Section title={stuck.title} data-id="element-485" data-mp="y2zkuyb6fskh">
          <div className="space-y-5 text-[17px] leading-[1.75] text-white/65 font-light" data-id="element-486" data-mp="y40ztdvbcz7h">
            {stuck.paragraphs.map((p, i) => <p key={i} className={i === stuck.paragraphs.length - 1 ? 'text-[16px] text-white/50' : undefined} data-id="element-487" data-mp="2dubk7hch62e6" data-mpi={i}>
                {p}
              </p>)}
          </div>

          <p className="mt-8 mb-4 text-[13px] uppercase tracking-wider text-white/40" data-id="element-488" data-mp="1zuse0u38514i">
            {stuck.compareLabel}
          </p>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-489" data-mp="1uwiksp128alj">
            {stuck.cases.map((c, i) => <div key={c.t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-490" data-mp="28vql5l8ylorb" data-mpi={i}>
                <div className="flex items-center gap-2.5 mb-2.5" data-id="element-491" data-mp="3k92qy1oa4hg4">
                  <span className="shrink-0 w-6 h-6 rounded-full border border-accent/40 bg-accent/10 text-accent font-semibold text-[12px] flex items-center justify-center" data-id="element-492" data-mp="3kbf6k86kefq0">
                    {i + 1}
                  </span>
                  <h3 className="text-white font-medium text-[15px]" data-id="element-493" data-mp="2hvof3go5kf4q">{c.t}</h3>
                </div>
                <p className="text-[14px] text-white/50 leading-relaxed font-light" data-id="element-494" data-mp="2lx9ogs1haba6">
                  {c.d}
                </p>
              </div>)}
          </div>
        </Section>

        {/* ---------- 5. PARA QUIÉN PUEDE SER ---------- */}
        <Section title={t.bestForTitle} data-id="element-495" data-mp="1u4iny5cri0f4">
          <p className="text-[16px] text-white/50 font-light mb-6" data-id="element-496" data-mp="1y7p2e0a12xwo">
            {t.bestForIntro}
          </p>
          <ul className="grid sm:grid-cols-2 gap-3" data-id="element-497" data-mp="2371nafhc9uwm">
            {bestFor.map(b => <li key={b} className="flex gap-3 items-start rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-[15px] text-white/75 leading-relaxed" data-id="element-498" data-mp="27yjsxgub4jnx">
                <Check className="w-[18px] h-[18px] text-accent shrink-0 mt-0.5" data-id="element-499" data-mp="1fgnv8oyr7w21" />
                {b}
              </li>)}
          </ul>
        </Section>

        {/* ---------- 6. KOVO EN RESUMEN ---------- */}
        <Section title={t.glanceTitle} data-id="element-500" data-mp="12zmtk0loy989">
          <div className="rounded-3xl border border-accent/25 bg-accent/[0.04] p-6 sm:p-8" data-id="element-501" data-mp="y00jg015t93o">
            <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-502" data-mp="tyf9onjx9axp">
              <span className="font-display text-3xl font-semibold" data-id="element-503" data-mp="ty5iiwn54xqf">
                {offer.brand}
              </span>
              <span className="text-[12px] font-medium text-accent border border-accent/40 rounded-full px-3 py-1" data-id="element-504" data-mp="p15cglqajuzb">
                {t.confidence} {offer.confidence}%
              </span>
            </div>

            {facts.length > 0 && <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-6" data-id="element-505" data-mp="ab7jlshf2h3s">
                {facts.map(f => <div key={f.label} className="flex items-baseline justify-between gap-4" data-id="element-506" data-mp="67j9b21eh7eu">
                    <span className="text-[14px] text-white/45" data-id="element-507" data-mp="1p6ftel9clr4x">{f.label}</span>
                    <span className="text-[15px] text-white font-medium text-right" data-id="element-508" data-mp="1p4cs352qwbkw">
                      {f.value}
                    </span>
                  </div>)}
              </div>}

            <div className="grid sm:grid-cols-2 gap-4 mb-7 pt-6 border-t border-white/10" data-id="element-509" data-mp="1k7cm0u5wb8ts">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5" data-id="element-510" data-mp="1k977c9l2x2ye">
                <p className="text-[12px] uppercase tracking-wider text-accent/90 mb-1.5" data-id="element-511" data-mp="16e4u11dwkbbk">
                  {t.glanceBestFor}
                </p>
                <p className="text-[15px] text-white/80 leading-relaxed" data-id="element-512" data-mp="witguni57c5x">
                  {pick(offer.glanceBestFor, lang)}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5" data-id="element-513" data-mp="nh3sjqjk4tcd">
                <p className="text-[12px] uppercase tracking-wider text-white/40 mb-1.5" data-id="element-514" data-mp="sg6taysi8dej">
                  {t.glanceNotFor}
                </p>
                <p className="text-[15px] text-white/60 leading-relaxed" data-id="element-515" data-mp="8v6azwpzcfds">
                  {pick(offer.glanceNotFor, lang)}
                </p>
              </div>
            </div>

            <Cta data-id="element-516" data-mp="hvbis7z35fsh" />
            <CtaMicro data-id="element-517" data-mp="hvupesjachr7" />
            <p className="mt-4 pt-4 border-t border-white/10 text-[12px] text-white/30 leading-relaxed" data-id="element-518" data-mp="hsys96f9hx3t">
              {ctaDisclaimer}
            </p>
          </div>
        </Section>

        {/* ---------- 7. CÓMO FUNCIONA ---------- */}
        <Section title={t.howTitle} data-id="element-519" data-mp="muo5mspyimv2">
          <ol className="space-y-3" data-id="element-520" data-mp="mtcyqk17syzq">
            {howItWorks.map((s, i) => <li key={s.t} className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-521" data-mp="11i0twp75f6p5" data-mpi={i}>
                <span className="shrink-0 w-9 h-9 rounded-full border border-accent/40 bg-accent/10 text-accent font-semibold text-[15px] flex items-center justify-center" data-id="element-522" data-mp="wmbt6ije7jr9">
                  {i + 1}
                </span>
                <div data-id="element-523" data-mp="1i3443utosx9t">
                  <h3 className="text-white font-medium text-[16px] mb-1.5" data-id="element-524" data-mp="1i2cgp7a8u5um">
                    {s.t}
                  </h3>
                  <p className="text-[15px] text-white/55 leading-relaxed font-light" data-id="element-525" data-mp="1e1isecsw2p17">
                    {s.d}
                  </p>
                </div>
              </li>)}
          </ol>
        </Section>

        {video && <Section title={t.videoTitle} data-id="element-526" data-mp="zcmmomeb1o3r">
            <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video" data-id="element-527" data-mp="zdnzowevsko4">
              <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube-nocookie.com/embed/${video}`} title={pick(offer.title, lang)} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-528" data-mp="ufmfg85pyfb2" />
            </div>
            <p className="mt-3 text-[13px] text-white/35" data-id="element-529" data-mp="bl7dc9o426j0">{t.videoNote}</p>
          </Section>}

        {/* ---------- 8. A FAVOR Y EN CONTRA ---------- */}
        <Section title={t.prosConsTitle} data-id="element-530" data-mp="2s25e4kgj587s">
          <div className="grid sm:grid-cols-2 gap-8" data-id="element-531" data-mp="2s382akmgwwk0">
            <div data-id="element-532" data-mp="2eba1cx2wu6yx">
              <h3 className="text-[13px] uppercase tracking-wider text-white/40 mb-4" data-id="element-533" data-mp="2ebtkgh4ar1ej">
                {t.pros}
              </h3>
              <ul className="space-y-3" data-id="element-534" data-mp="36151zpw7xj2w">
                {pros.map(p => <li key={p} className="flex gap-2.5 text-[15px] text-white/70 leading-relaxed" data-id="element-535" data-mp="35z21ud1cq9kg">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-1" data-id="element-536" data-mp="2wzf35z00bhv3" />
                    {p}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-537" data-mp="1lks2675b4pqs">
              <h3 className="text-[13px] uppercase tracking-wider text-white/40 mb-4" data-id="element-538" data-mp="1lk9wyl0o316q">
                {t.cons}
              </h3>
              <ul className="space-y-3" data-id="element-539" data-mp="1qlpi0d2uh7p4">
                {cons.map(c => <li key={c} className="flex gap-2.5 text-[15px] text-white/70 leading-relaxed" data-id="element-540" data-mp="3s88vwc8kwfty">
                    <X className="w-4 h-4 text-white/30 shrink-0 mt-1" data-id="element-541" data-mp="3ijimifb7lpkx" />
                    {c}
                  </li>)}
              </ul>
            </div>
          </div>
        </Section>

        {/* ---------- 9. LO QUE NO HACE ---------- */}
        <Section title={t.notDoTitle} data-id="element-542" data-mp="39kdnz8zndw8r">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-543" data-mp="34k94da3g0leb">
            <div className="flex items-center gap-2 mb-4" data-id="element-544" data-mp="34mc2kher1ku4">
              <AlertCircle className="w-[18px] h-[18px] text-accent" data-id="element-545" data-mp="2zlepgj8ljgzh" />
              <h3 className="text-white font-medium text-[15px]" data-id="element-546" data-mp="2vk2x45yc8hnl">
                {t.notDoTitle}
              </h3>
            </div>
            <ul className="space-y-3" data-id="element-547" data-mp="xt004z3q3a91">
              {t.notDo.map(item => <li key={item} className="flex gap-3 text-[15px] text-white/55 leading-relaxed" data-id="element-548" data-mp="tszhf29lccw6">
                  <X className="w-4 h-4 text-white/30 shrink-0 mt-1" data-id="element-549" data-mp="2gonze8k81uef" />
                  {item}
                </li>)}
            </ul>
            <p className="mt-5 pt-5 border-t border-white/10 text-[15px] text-white/55 leading-relaxed" data-id="element-550" data-mp="2lgfjc08vh1vl">
              {t.notDoNote}
            </p>
          </div>
        </Section>

        {/* ---------- 10. PARA QUIÉN NO ES ---------- */}
        <Section title={t.notForTitle} data-id="element-551" data-mp="1st1qoec7isha">
          <p className="text-[16px] text-white/50 font-light mb-6" data-id="element-552" data-mp="1xp0adomlbmbd">
            {t.notForIntro}
          </p>
          <ul className="space-y-4" data-id="element-553" data-mp="21qc0rwdagbkm">
            {notFor.map(n => <li key={n} className="flex gap-3 text-[16px] text-white/65 leading-relaxed font-light" data-id="element-554" data-mp="26q83zte5vyz4">
                <span className="text-accent shrink-0" data-id="element-555" data-mp="3d6ah8laspzvv">—</span>
                {n}
              </li>)}
          </ul>
        </Section>

        {/* ---------- 11. FAQ ---------- */}
        <Section title={t.faqTitle} data-id="element-556" data-mp="3gozt39utgsy9">
          <div className="space-y-3" data-id="element-557" data-mp="3bpd648p6ryc9">
            {faq.map(f => <details key={f.q} className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-id="element-558" data-mp="3br7phikt7ie8">
                <summary className="cursor-pointer list-none px-5 py-4 text-[16px] text-white/85 font-medium flex items-center justify-between gap-4 min-h-[56px]" data-id="element-559" data-mp="3lmhtgburpzun">
                  {f.q}
                  <span className="text-accent text-xl shrink-0 group-open:rotate-45 transition-transform duration-200" data-id="element-560" data-mp="3up0vitqqc8fu">
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 text-[15px] text-white/55 leading-relaxed font-light" data-id="element-561" data-mp="884s5zxkximt">
                  {f.a}
                </p>
              </details>)}
          </div>
        </Section>

        {/* ---------- 12. CÓMO LO EVALUAMOS + 13. FRAMEWORK ---------- */}
        <Section title={t.criteriaTitle} data-id="element-562" data-mp="10ve6ynyjf9hy">
          <div className="grid sm:grid-cols-2 gap-4 mb-10" data-id="element-563" data-mp="10wftppox1kfi">
            {t.criteria.map(c => <div key={c.t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-564" data-mp="15swf4c4qdkow">
                <h3 className="text-white font-medium mb-2 text-[15px]" data-id="element-565" data-mp="vw1jc0zudyeb">
                  {c.t}
                </h3>
                <p className="text-[14px] text-white/50 leading-relaxed font-light" data-id="element-566" data-mp="i44xgeorokqd">
                  {c.d}
                </p>
              </div>)}
          </div>
          <DecisionFramework lang={lang} data-id="element-567" data-mp="86h15kbij563" />
        </Section>

        {/* ---------- 14. CTA FINAL ---------- */}
        <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 text-center" data-id="element-568" data-mp="d5aencqsuw69">
          <h2 className="font-display text-2xl font-semibold mb-3" data-id="element-569" data-mp="3van4a5j1kn4z">
            {t.finalTitle}
          </h2>
          <p className="text-[15px] text-white/50 mb-7 font-light max-w-md mx-auto leading-relaxed" data-id="element-570" data-mp="4556v1hp1vrk">
            {t.finalText}
          </p>
          <Cta full data-id="element-571" data-mp="25vcgm6gci2ea" />
          <p className="mt-3 text-[13px] text-white/35 leading-relaxed" data-id="element-572" data-mp="25vw0vttgx2vh">
            {ctaMicro}
          </p>
          <p className="mt-5 pt-5 border-t border-white/10 text-[12px] text-white/30 leading-relaxed max-w-lg mx-auto" data-id="element-573" data-mp="1w483lca8crm3">
            {ctaDisclaimer}
          </p>
        </section>

        {/* ---------- 15. PIE LEGAL ---------- */}
        <p className="mt-12 text-[13px] text-white/35 leading-relaxed text-center" data-id="element-574" data-mp="2oruez6pp90ji">
          {t.affiliateNote(offer.network)}
        </p>
        <p className="mt-3 text-[13px] text-white/35 leading-relaxed text-center" data-id="element-575" data-mp="2jrpxbdd21zrp">
          {t.footerNote}{' '}
          <Link to={withQuery('/metodologia', search)} className="text-accent hover:underline" data-id="element-576" data-mp="2fu1zo2jz9sw4">
            {t.methodology}
          </Link>{' '}
          {t.and}{' '}
          <Link to={withQuery('/divulgacion-de-afiliados', search)} className="text-accent hover:underline" data-id="element-577" data-mp="zdz1xldqn5tl">
            {t.disclosurePage}
          </Link>
          .
        </p>
      </article>

      {/* Barra fija en móvil */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 px-4 pt-3 pb-4 bg-background/90 backdrop-blur-lg border-t border-white/10" data-id="element-578" data-mp="3g0x8cruoia28">
        <a href={href} rel="nofollow sponsored noopener noreferrer" onClick={fireDirect} className="w-full inline-flex items-center justify-center gap-2 bg-accent text-background h-[52px] rounded-full text-[16px] font-semibold active:scale-[0.98] transition-transform" data-id="element-579" data-mp="3osh4d67xm5ti">
          {ctaLabel}
          <ArrowRight className="w-[18px] h-[18px]" data-id="element-580" data-mp="7lx39ra94c9n" />
        </a>
        <p className="mt-2 text-[11px] text-white/30 text-center leading-snug" data-id="element-581" data-mp="bmpeur6lit93">
          {ctaMicro}
        </p>
      </div>

      <Footer data-id="element-582" data-mp="29d0fum3pc0wu" />
    </div>;
}